import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import {useBeforeunload} from 'react-beforeunload';
import {Prompt} from 'react-router-dom';
import {makeStyles} from '@material-ui/core';
import {PageLayoutWithFixedAreas} from 'layout';
import {FilesViewAllHeader} from '../FilesViewAllHeader/FilesViewAllHeader';
import {FilesViewAllFooter} from '../FilesViewAllFooter/FilesViewAllFooter';
import {Button, CircularProgress, Loading, Table, Tooltip, Wrapper} from 'components-lib';
import {VerticalDropdown} from 'admin/resources/files/components/Dropdowns';
import {AdminWithoutStaffRoles, USAAndSuperAdminRoles, usePermissions} from 'permissions';
import {IColumn} from 'models/library/common';
import {IBaseFileItem} from 'models';
import {useFilesAll, useFilesAllStoreActions} from '../../hooks';
import {FileAllTableColumnsEnum} from '../../enums';
import {filesAllMessages, filesDropdownList, getFilteredFilesColumns, mapFilesDropdownList} from '../../utils';

import {IBaseFileItemStatusEnum} from 'admin/resources/enums';

const fileColumns: IColumn[] = [
  {id: 'name', label: 'Name', className: 'files-col-name'},
  {id: 'type', label: 'Resource Type'},
  {id: 'username', label: 'User Name'},
  {id: 'status', label: 'Status', className: 'files-col-status'},
  {id: 'viewGroup', label: '', align: 'right', className: 'files-col-actions'},
];

/** Ellipsis name; tooltip only when the rendered text is actually truncated. */
const TruncatedName: FC<{text: string; className: string}> = ({text, className}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <Tooltip
      title={text}
      size="large"
      dense
      disableHoverListener
      open={tooltipOpen}
      onClose={() => setTooltipOpen(false)}
    >
      <span
        ref={textRef}
        className={className}
        onMouseEnter={() => {
          const el = textRef.current;
          if (el && el.scrollWidth > el.clientWidth) {
            setTooltipOpen(true);
          }
        }}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        {text}
      </span>
    </Tooltip>
  );
};

export const FilesViewAllContent = () => {
  const classes = useStyles();
  const {
    loading,
    filesAllIds,
    filesAllEntities,
    fileDownloading,
    downloadingId,
    fetchFilesAllPaged,
    redirectToEditFilePage,
  } = useFilesAll();
  const {clearFiles} = useFilesAllStoreActions();
  const {hasPermission} = usePermissions();

  const shouldFilterColumnsBasedOnUsername = !hasPermission(AdminWithoutStaffRoles);
  const shouldFilterColumnsBasedOnStatus = !hasPermission(USAAndSuperAdminRoles);
  const shouldDisableViewExpiredResource = !hasPermission(USAAndSuperAdminRoles);

  useEffect(() => {
    fetchFilesAllPaged();

    return () => {
      clearFiles();
    };
  }, [fetchFilesAllPaged, clearFiles]);

  useBeforeunload(() => {
    if (fileDownloading) {
      return filesAllMessages.warningUnsavedChanges;
    }
  });

  const columns = useMemo(() => {
    let filteredCols = [...fileColumns];

    if (shouldFilterColumnsBasedOnUsername) {
      filteredCols = getFilteredFilesColumns(filteredCols, FileAllTableColumnsEnum.UserName);
    }

    if (shouldFilterColumnsBasedOnStatus) {
      filteredCols = getFilteredFilesColumns(filteredCols, FileAllTableColumnsEnum.Status);
    }

    return filteredCols;
  }, [shouldFilterColumnsBasedOnUsername, shouldFilterColumnsBasedOnStatus]);

  const rows = useMemo(() => {
    if (!filesAllIds.length) {
      return [];
    }

    return filesAllIds.map((key: string) => {
      const file = filesAllEntities[key] as IBaseFileItem;
      const {title, contentType, contentItemId, username, status} = file;

      const isFileExpired = status === IBaseFileItemStatusEnum.Expired;
      const shouldDisableButtons = isFileExpired && shouldDisableViewExpiredResource;

      const dropdownItems = mapFilesDropdownList(filesDropdownList(), isFileExpired);

      const name = title ?? '';

      return {
        name: <TruncatedName text={name} className={classes.nameCell} />,
        type: contentType,
        username: username ?? '',
        status: status ?? '',
        viewGroup: (
          <div className={classes.actionsRow}>
            <Button.Secondary size="small" clickHandler={() => redirectToEditFilePage(contentItemId)}>
              View
            </Button.Secondary>
            {fileDownloading && downloadingId === contentItemId ? (
              <Wrapper className={classes.progress}>
                <CircularProgress size={20} />
              </Wrapper>
            ) : (
              <VerticalDropdown file={file} dropdownItems={dropdownItems} shouldDisable={shouldDisableButtons} />
            )}
          </div>
        ),
      };
    });
  }, [
    filesAllIds,
    filesAllEntities,
    redirectToEditFilePage,
    shouldDisableViewExpiredResource,
    downloadingId,
    fileDownloading,
    classes.progress,
    classes.actionsRow,
    classes.nameCell,
  ]);

  return (
    <PageLayoutWithFixedAreas headerSize="small" header={<FilesViewAllHeader />} footer={<FilesViewAllFooter />}>
      {loading ? (
        <Loading />
      ) : (
        <div className={classes.table}>
          <Table rows={rows} columns={columns} />
        </div>
      )}
      <Prompt when={fileDownloading} message={filesAllMessages.warningUnsavedChanges} />
    </PageLayoutWithFixedAreas>
  );
};

const useStyles = makeStyles(() => ({
  progress: {
    padding: 12,
  },
  // Keep View + overflow menu on one line when the table/window is narrow.
  actionsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  nameCell: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  // Denser rows (keep View button size); MUI default cell padding is 16px.
  table: {
    '& .MuiTable-root': {
      tableLayout: 'fixed',
      width: '100%',
    },
    '& .MuiTableCell-root': {
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 10,
      paddingRight: 10,
    },
    // Name column: constrain width so ellipsis can apply (Tooltip adds a wrapper span).
    '& .files-col-name': {
      width: '35%',
      overflow: 'hidden',
      '& > span': {
        display: 'block',
        maxWidth: '100%',
        overflow: 'hidden',
      },
    },
    // Keep Status from overlapping the actions column.
    '& .files-col-status': {
      width: '12%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    // Actions: reserved width for View + overflow icon on one line.
    '& .files-col-actions': {
      whiteSpace: 'nowrap',
      width: 140,
      minWidth: 140,
      maxWidth: 140,
      overflow: 'hidden',
    },
  },
}));
